import presentation from '../../constants/studioPresentation.json';
import React, { useState, useEffect } from 'react';
import styles from './WeatherLine.module.css';
import { useTranslation } from '../../i18n/useTranslation';

/**
 * WeatherLine - 일주일 날씨를 두 줄로 표시
 * Paris 4 rue dunois 75013 (위도: 48.8338, 경도: 2.3654)
 */
const WEATHER_DAYS_FIRST_ROW = 4;

const WeatherLine = () => {
    const [weather, setWeather] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWeather = async () => {
            try {
                // Open-Meteo API (무료, API 키 불필요)
                // Paris 13구 좌표
                const lat = presentation.weather.latitude;
                const lon = presentation.weather.longitude;

                const response = await fetch(
                    `${presentation.weather.url}?latitude=${lat}&longitude=${lon}&daily=${presentation.weather.daily}&timezone=${presentation.weather.timeZone}`
                );

                const data = await response.json();

                if (data.daily) {
                    const days = data.daily.time.slice(0, presentation.weather.days).map((date, i) => ({
                        date: new Date(date),
                        code: data.daily.weather_code[i],
                        max: Math.round(data.daily.temperature_2m_max[i]),
                        min: Math.round(data.daily.temperature_2m_min[i])
                    }));
                    setWeather(days);
                }
            } catch (err) {
                console.error('Weather fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchWeather();
    }, []);

    // WMO 날씨 코드를 SVG 아이콘으로 변환
    const getWeatherIcon = (code, isToday = false) => {
        const size = 12;
        const color = isToday ? '#333' : '#888';

        // 맑음
        if (code === 0) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
            </svg>
        );
        // 구름 조금
        if (code <= 3) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
            </svg>
        );
        // 안개
        if (code <= 48) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <line x1="3" y1="8" x2="21" y2="8" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="16" x2="21" y2="16" />
            </svg>
        );
        // 비
        if (code <= 67 || (code >= 80 && code <= 82)) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <line x1="16" y1="13" x2="16" y2="21" />
                <line x1="8" y1="13" x2="8" y2="21" />
                <line x1="12" y1="15" x2="12" y2="23" />
                <path d="M20 16.58A5 5 0 0018 7h-1.26A8 8 0 104 15.25" />
            </svg>
        );
        // 눈
        if (code <= 77 || (code >= 85 && code <= 86)) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <path d="M20 17.58A5 5 0 0018 8h-1.26A8 8 0 104 16.25" />
                <line x1="8" y1="16" x2="8.01" y2="16" />
                <line x1="8" y1="20" x2="8.01" y2="20" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
                <line x1="12" y1="22" x2="12.01" y2="22" />
                <line x1="16" y1="16" x2="16.01" y2="16" />
                <line x1="16" y1="20" x2="16.01" y2="20" />
            </svg>
        );
        // 번개
        if (code >= 95) return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <path d="M19 16.9A5 5 0 0018 7h-1.26a8 8 0 10-11.62 9" />
                <polyline points="13 11 9 17 15 17 11 23" />
            </svg>
        );
        // 기본 (구름)
        return (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
                <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
            </svg>
        );
    };

    const { t } = useTranslation();

    // 요일 약어 (다국어)
    const getDayName = (date) => {
        const days = t('weather.days') || ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
        return days[date.getDay()];
    };

    if (loading) {
        return <div className={styles.container}>...</div>;
    }

    // 오늘 날짜 체크용
    const today = new Date().toDateString();
    const weatherRows = [
        weather.slice(0, WEATHER_DAYS_FIRST_ROW),
        weather.slice(WEATHER_DAYS_FIRST_ROW)
    ].filter(row => row.length > 0);

    return (
        <div className={styles.container}>
            {weatherRows.map((row, rowIndex) => (
                <div key={rowIndex} className={styles.row}>
                    {row.map((day, i) => {
                        const isToday = day.date.toDateString() === today;
                        return (
                            <span key={`${rowIndex}-${i}`} className={`${styles.day} ${isToday ? styles.today : ''}`}>
                                <span className={styles.dayName}>{getDayName(day.date)}</span>
                                <span className={styles.icon}>{getWeatherIcon(day.code, isToday)}</span>
                                <span className={styles.temp}>{day.max}°</span>
                            </span>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default WeatherLine;
